import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { IUser, AuthTokens } from '@barrikadave/shared';
import { UserRole } from '@barrikadave/shared';
import { authApi } from '../services/api';
import { setupSocket, teardownSocket } from '../services/socket';

interface AuthContextType {
  user: IUser | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALLOWED_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.STAF];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<IUser | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!tokens;

  const logout = useCallback(() => {
    setUser(null);
    setTokens(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    teardownSocket();
  }, []);

  const loadUser = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!accessToken || !refreshToken) {
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = await authApi.me();

      if (!ALLOWED_ROLES.includes(currentUser.role)) {
        logout();
        setIsLoading(false);
        return;
      }

      setUser(currentUser);
      setTokens({ accessToken, refreshToken });
      setupSocket(accessToken);
    } catch {
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await authApi.login(email, password);

      if (!ALLOWED_ROLES.includes(result.user.role)) {
        throw new Error('Nuk keni akses ne panelin e administrimit. Vetem ADMIN mund te hyje.');
      }

      localStorage.setItem('accessToken', result.tokens.accessToken);
      localStorage.setItem('refreshToken', result.tokens.refreshToken);
      setUser(result.user);
      setTokens(result.tokens);
      setupSocket(result.tokens.accessToken);
    },
    [],
  );

  const clearMustChangePassword = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated,
        isLoading,
        login,
        logout,
        clearMustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
